import { Controller } from '@hotwired/stimulus';
import { debounce, useEventListener } from 'stimulus-library';
import { clamp } from '../../bundles/utilities/clamp';

export default class extends Controller {
  static values = { currentTab: Number, equalize: Boolean };

  static targets = ['link', 'content'];

  static classes = [
    'contentActive',
    'contentHide',
    'linkActive',
    'linkHide',
  ];

  get _anchor() {
    return window.location.hash.substring(1);
  }

  set _anchor(value) {
    window.location.hash = value;
  }

  get currentTab() {
    if (this.hasCurrentTabValue) {
      return this.currentTabValue;
    }
    return this.linkTargets.findIndex((link) => link.classList.contains(this.activeClass));
  }

  get equalize() {
    return this.hasEqualizeValue ? this.equalizeValue : false;
  }

  initialize() {
    this.switchTabs = this.switchTabs.bind(this);
    this.setMinHeight = this.setMinHeight.bind(this);
    this.analyticsTabSelected = debounce(this.analyticsTabSelected.bind(this), 500);
  }

  connect() {
    Array.from(this.linkTargets).forEach((link) => {
      link.addEventListener('click', this.switchTabs);
    });
    useEventListener(this, window, 'hashchange', this._checkAnchor);

    if (this.equalize) {
      this.setMinHeight();
    }

    this.currentTabValue = this.currentTab;
    this._checkAnchor();
  }

  switchTabs(event) {
    if ((event.currentTarget.href && event.currentTarget.href.indexOf('#') > 0) || (event.currentTarget.href && event.currentTarget.href.indexOf(window.location.href) === -1)) {
      // If the target is not an anchor link, or a fully qualified URL, then we don't want to do anything
      return;
    }

    event.preventDefault();

    if (event.currentTarget.dataset.tabName) {
      this._anchor = event.currentTarget.dataset.tabName;
    }
    this.currentTabValue = this.linkTargets.indexOf(event.currentTarget);
  }

  _checkAnchor() {
    Array.from(this.linkTargets).some((link, index) => {
      if (link.dataset.tabName === this._anchor) {
        this.currentTabValue = index;
        return true;
      }
      return false;
    });
  }

  currentTabValueChanged() {
    let index = this.currentTab;
    index = this.clampIndex(index);
    this.selectTab(index);
  }

  selectTab(index) {
    const clampedIndex = this.clampIndex(index);
    const links = this.linkTargets;
    const panels = this.contentTargets;
    const activePanel = panels[clampedIndex];
    const activeLink = links[clampedIndex];
    const otherPanels = [...panels.slice(0, clampedIndex), ...panels.slice(clampedIndex + 1)];
    const otherLinks = [...links.slice(0, clampedIndex), ...links.slice(clampedIndex + 1)];

    this.analyticsTabSelected(activeLink);
    this.addLinkActiveClasses(activeLink);
    this.removeLinkHideClasses(activeLink);
    activeLink.setAttribute('aria-selected', 'true');

    this.addContentActiveClasses(activePanel);
    this.removeContentHideClasses(activePanel);

    otherLinks.forEach((link) => {
      link.removeAttribute('aria-selected');
      this.removeLinkActiveClasses(link);
      this.addLinkHideClasses(link);
    });
    otherPanels.forEach((panel) => {
      this.removeContentActiveClasses(panel);
      this.addContentHideClasses(panel);
    });
  }

  analyticsTabSelected(activeLink) {
    if (window.analytics) {
      window.analytics.track('Tab Selected', {
        category: 'Tabs',
        label: activeLink.textContent,
        path: window.location.pathname,
        referrer: document.referrer,
        search: window.location.search,
        url: window.location.href,
      });
    }
  }

  clampIndex(index) {
    return clamp(index, 0, this.contentTargets.length - 1);
  }

  addContentActiveClasses(element) {
    this.contentActiveClasses.forEach((klass) => element.classList.add(klass));
  }

  removeContentActiveClasses(element) {
    this.contentActiveClasses.forEach((klass) => element.classList.remove(klass));
  }

  addLinkActiveClasses(element) {
    this.linkActiveClasses.forEach((klass) => element.classList.add(klass));
  }

  removeLinkActiveClasses(element) {
    this.linkActiveClasses.forEach((klass) => element.classList.remove(klass));
  }

  addContentHideClasses(element) {
    this.contentHideClasses.forEach((klass) => element.classList.add(klass));
  }

  removeContentHideClasses(element) {
    this.contentHideClasses.forEach((klass) => element.classList.remove(klass));
  }

  addLinkHideClasses(element) {
    this.linkHideClasses.forEach((klass) => element.classList.add(klass));
  }

  removeLinkHideClasses(element) {
    this.linkHideClasses.forEach((klass) => element.classList.remove(klass));
  }

  setMinHeight() {
    let minHeight = 0;

    // determine the minimum height
    this.contentTargets.forEach((content) => {
      const hidden = content.hasAttribute('tab-hidden');
      if (hidden) {
        this.removeHideClasses(content);
      }

      const height = content.offsetHeight;
      if (height > minHeight) {
        minHeight = height;
      }

      if (hidden) {
        this.addHideClasses(content);
      }
    });

    // apply to all tabs
    this.contentTargets.forEach((content) => {
      const contentStyle = content.style;
      contentStyle.minHeight = `${minHeight}px`;
    });
  }
}
